import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarCheck, Sparkles, Wrench, DollarSign, TrendingUp,
  LogIn, LogOut, Users, Bell, ArrowUpRight, ChevronLeft, CircleDot, Ticket,
} from "lucide-react";
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, money, authToken } from "@/lib/api";

const NAV = [
  { icon: LayoutDashboard, label: "Dashboard", active: true },
  { icon: CalendarCheck, label: "Reservations" },
  { icon: Sparkles, label: "Housekeeping" },
  { icon: Wrench, label: "Maintenance" },
  { icon: DollarSign, label: "Revenue" },
  { icon: Users, label: "Guests & Owners" },
];
const COLORS = ["#0A0A0A", "#C19A6B", "#8B7A6E", "#D4AF37"];

const StatusBadge = ({ s }) => {
  const map = { "Complete": "bg-green-100 text-green-700", "In Progress": "bg-amber-100 text-amber-700", "Scheduled": "bg-blue-100 text-blue-700", "Assigned": "bg-amber-100 text-amber-700", "Open": "bg-gray-100 text-gray-600" };
  return <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${map[s] || "bg-gray-100 text-gray-600"}`}>{s}</span>;
};
const PriorityBadge = ({ p }) => {
  const map = { High: "text-red-600", Medium: "text-amber-600", Low: "text-gray-500" };
  return <span className={`flex items-center gap-1 text-xs font-semibold ${map[p]}`}><CircleDot size={11} /> {p}</span>;
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const logout = () => { authToken.clear(); nav("/admin/login"); };
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="flex min-h-screen items-center justify-center text-taupe">Loading dashboard…</div>;
  const k = d.kpis;

  return (
    <div className="flex min-h-screen bg-sand">
      {/* SIDEBAR */}
      <aside className="hidden w-64 shrink-0 border-r border-black/10 bg-white p-5 lg:block">
        <Link to="/" className="flex flex-col leading-none">
          <span className="text-xl font-semibold text-ink">Elite STR</span>
          <span className="overline mt-0.5 text-[9px]">PMS Dashboard</span>
        </Link>
        <nav className="mt-8 space-y-1">
          {NAV.map((n) => (
            <button key={n.label} data-testid={`admin-nav-${n.label.toLowerCase().split(" ")[0]}`}
              className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${n.active ? "bg-ink text-white" : "text-ink/70 hover:bg-cream"}`}>
              <n.icon size={18} strokeWidth={1.5} /> {n.label}
            </button>
          ))}
        </nav>
        <Link to="/admin/referrals" data-testid="nav-referrals"
          className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-ink/70 transition-colors hover:bg-cream">
          <Ticket size={18} strokeWidth={1.5} /> Referral Codes
        </Link>
        <button data-testid="admin-logout-btn" onClick={logout}
          className="mt-8 flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-taupe hover:bg-cream">
          <LogOut size={16} /> Log out
        </button>
        <Link to="/" className="mt-1 flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm text-taupe hover:bg-cream">
          <ChevronLeft size={16} /> Back to site
        </Link>
      </aside>

      {/* MAIN */}
      <div className="flex-1 overflow-x-hidden">
        <header className="flex items-center justify-between border-b border-black/10 bg-white px-6 py-4">
          <div>
            <h1 className="text-2xl">Good morning, Alexandra</h1>
            <p className="text-sm text-taupe">Here's what's happening across your portfolio today.</p>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative rounded-full border border-black/10 p-2"><Bell size={18} strokeWidth={1.5} /><span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-camel" /></button>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-ink text-xs font-bold text-white">AR</div>
          </div>
        </header>

        <div className="p-6">
          {/* KPIs */}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[
              { icon: DollarSign, label: "Revenue (MTD)", value: money(k.revenue_mtd), change: `+${k.revenue_change}%` },
              { icon: TrendingUp, label: "Occupancy", value: `${k.occupancy}%`, change: `+${k.occupancy_change}%` },
              { icon: LogIn, label: "Arrivals today", value: k.arrivals_today, change: `${k.departures_today} departures` },
              { icon: Wrench, label: "Open tickets", value: k.open_tickets, change: `${k.active_properties} properties` },
            ].map((c, i) => (
              <div key={i} data-testid={`kpi-${i}`} className="rounded-2xl border border-black/5 bg-white p-5">
                <div className="flex items-center justify-between">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cream"><c.icon size={16} strokeWidth={1.5} /></span>
                  <span className="text-xs font-medium text-camel">{c.change}</span>
                </div>
                <div className="mt-3 text-3xl font-medium text-ink">{c.value}</div>
                <div className="overline mt-1">{c.label}</div>
              </div>
            ))}
          </div>

          {/* CHARTS */}
          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            <div className="rounded-2xl border border-black/5 bg-white p-5 lg:col-span-2">
              <div className="mb-4 flex items-center justify-between"><h2 className="text-lg font-semibold text-ink">Revenue & occupancy</h2><span className="text-xs text-taupe">Last 6 months</span></div>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={d.revenue_trend}>
                  <defs><linearGradient id="rev" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#C19A6B" stopOpacity={0.35} /><stop offset="95%" stopColor="#C19A6B" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#8B7A6E" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "#8B7A6E" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Area type="monotone" dataKey="revenue" stroke="#C19A6B" strokeWidth={2} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-2xl border border-black/5 bg-white p-5">
              <h2 className="mb-4 text-lg font-semibold text-ink">Booking channels</h2>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={d.channel_mix} dataKey="value" nameKey="channel" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {d.channel_mix.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `${v}%`} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 space-y-1.5">
                {d.channel_mix.map((c, i) => (
                  <div key={c.channel} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-ink/70"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} /> {c.channel}</span>
                    <span className="font-medium text-ink">{c.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* TABLES */}
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <Panel icon={LogIn} title="Today's arrivals">
              {d.arrivals.map((a, i) => (
                <Row3 key={i} a={a.guest} b={a.property} c={`${a.guests} guests · ${a.time}`} tag={`${a.nights}n`} />
              ))}
            </Panel>
            <Panel icon={LogOut} title="Today's departures">
              {d.departures.map((a, i) => (
                <Row3 key={i} a={a.guest} b={a.property} c={`${a.guests} guests · ${a.time}`} />
              ))}
            </Panel>
            <Panel icon={Sparkles} title="Housekeeping status">
              {d.cleaning.map((c, i) => (
                <div key={i} className="flex items-center justify-between border-b border-black/5 py-3 last:border-0">
                  <div><div className="text-sm font-medium text-ink">{c.property}</div><div className="text-xs text-taupe">{c.cleaner} · ETA {c.eta}</div></div>
                  <StatusBadge s={c.status} />
                </div>
              ))}
            </Panel>
            <Panel icon={Wrench} title="Maintenance tickets">
              {d.maintenance.map((m, i) => (
                <div key={i} className="flex items-center justify-between border-b border-black/5 py-3 last:border-0">
                  <div><div className="text-sm font-medium text-ink">{m.issue}</div><div className="text-xs text-taupe">{m.property} · {m.vendor}</div></div>
                  <div className="flex flex-col items-end gap-1"><PriorityBadge p={m.priority} /><StatusBadge s={m.status} /></div>
                </div>
              ))}
            </Panel>
          </div>

          {/* PROPERTY PERFORMANCE */}
          <div className="mt-6 rounded-2xl border border-black/5 bg-white p-5">
            <h2 className="mb-4 text-lg font-semibold text-ink">Property performance</h2>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={d.property_perf} layout="vertical" margin={{ left: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#8B7A6E" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
                <YAxis type="category" dataKey="property" width={140} tick={{ fontSize: 11, fill: "#0A0A0A" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => money(v)} />
                <Bar dataKey="revenue" fill="#0A0A0A" radius={[0, 6, 6, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

const Panel = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-black/5 bg-white p-5">
    <h2 className="mb-2 flex items-center gap-2 text-lg font-semibold text-ink"><Icon size={18} strokeWidth={1.5} className="text-camel" /> {title}</h2>
    <div>{children}</div>
  </div>
);
const Row3 = ({ a, b, c, tag }) => (
  <div className="flex items-center justify-between border-b border-black/5 py-3 last:border-0">
    <div><div className="text-sm font-medium text-ink">{a}</div><div className="text-xs text-taupe">{b} · {c}</div></div>
    {tag && <span className="rounded-full bg-cream px-2.5 py-1 text-[11px] font-medium text-ink">{tag}</span>}
  </div>
);
